import React, { useMemo, useState, useCallback } from 'react';
import { Package, Plus, X, Archive, AlertCircle } from 'lucide-react';
import { Input } from '../ui/input';
import { useCatalogItems } from '../../hooks/useCatalogItems';

/**
 * DefaultItemsPicker
 *
 * Multi-select of active Catalog Items with an optional expected
 * quantity per row. Used by Service Program step editor and by the
 * WorkOrderSheet "Customize This Visit" flow.
 *
 * Value shape:
 *   [{ itemId: string, quantity: number | null }]
 *
 * Archived-item semantics:
 *   • Archived items DO NOT appear in the picker for new additions.
 *   • Archived items ALREADY in `value` remain displayed with an
 *     "Archived" chip, are preserved on save, and are removable/
 *     replaceable via the standard row X button.
 *
 * Props:
 *   value      — current items array (as above)
 *   onChange   — (nextValue) => void
 *   emptyLabel — text shown when list is empty (default: "No items selected")
 *   testIdPrefix — data-testid prefix for the rows (default: "items-picker")
 */
const DefaultItemsPicker = ({
  value,
  onChange,
  emptyLabel = 'No items selected',
  testIdPrefix = 'items-picker',
}) => {
  const { items: allItems, byId, activeItems, loading } = useCatalogItems();

  const [addQuery, setAddQuery] = useState('');
  const [showAdder, setShowAdder] = useState(false);

  const rows = Array.isArray(value) ? value : [];
  const selectedIds = useMemo(() => new Set(rows.map(r => r.itemId)), [rows]);

  const addableOptions = useMemo(() => {
    const q = addQuery.trim().toLowerCase();
    return activeItems
      .filter((it) => !selectedIds.has(it.id))
      .filter((it) => {
        if (!q) return true;
        const hay = `${it.name || ''} ${it.sku || ''} ${it.category || ''}`.toLowerCase();
        return hay.includes(q);
      })
      .slice(0, 30);
  }, [activeItems, addQuery, selectedIds]);

  const handleAdd = useCallback((itemId) => {
    if (!itemId || selectedIds.has(itemId)) return;
    onChange([...rows, { itemId, quantity: null }]);
    setAddQuery('');
  }, [rows, selectedIds, onChange]);

  const handleRemove = useCallback((idx) => {
    const next = rows.filter((_, i) => i !== idx);
    onChange(next);
  }, [rows, onChange]);

  const handleQty = useCallback((idx, val) => {
    const parsed = val === '' ? null : Number(val);
    const q = Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
    const next = rows.map((r, i) => (i === idx ? { ...r, quantity: q } : r));
    onChange(next);
  }, [rows, onChange]);

  return (
    <div data-testid={`${testIdPrefix}-root`}>
      {rows.length === 0 ? (
        <div className="text-xs text-gray-400 italic py-1" data-testid={`${testIdPrefix}-empty`}>
          {emptyLabel}
        </div>
      ) : (
        <div className="space-y-1.5" data-testid={`${testIdPrefix}-rows`}>
          {rows.map((row, idx) => {
            const item = byId[row.itemId];
            const isArchived = item && item.active === false;
            const isMissing = !item;
            return (
              <div
                key={`${row.itemId}-${idx}`}
                className={`flex items-center gap-2 rounded-md border px-2 py-1.5 ${
                  isArchived
                    ? 'border-amber-200 bg-amber-50/50'
                    : isMissing
                      ? 'border-red-200 bg-red-50/50'
                      : 'border-gray-200 bg-white'
                }`}
                data-testid={`${testIdPrefix}-row-${idx}`}
              >
                <Package className={`w-3.5 h-3.5 flex-shrink-0 ${isArchived ? 'text-amber-600' : isMissing ? 'text-red-600' : 'text-gray-500'}`} />
                <div className="flex-1 min-w-0">
                  <div className={`text-xs font-medium truncate ${isMissing ? 'text-red-700' : 'text-gray-800'}`}>
                    {item?.name || '(unknown item)'}
                    {item?.sku && <span className="ml-1 text-[10px] text-gray-400 font-normal">· {item.sku}</span>}
                    {isArchived && (
                      <span className="ml-1.5 inline-flex items-center gap-0.5 text-[10px] font-semibold text-amber-700" data-testid={`${testIdPrefix}-row-${idx}-archived-chip`}>
                        <Archive className="w-2.5 h-2.5" />
                        Archived
                      </span>
                    )}
                    {isMissing && (
                      <span className="ml-1.5 inline-flex items-center gap-0.5 text-[10px] font-semibold text-red-700">
                        <AlertCircle className="w-2.5 h-2.5" />
                        Missing
                      </span>
                    )}
                  </div>
                </div>
                <Input
                  type="number"
                  min="0"
                  step="any"
                  value={row.quantity ?? ''}
                  onChange={(e) => handleQty(idx, e.target.value)}
                  placeholder="Qty"
                  className="w-16 h-7 text-xs px-1.5"
                  data-testid={`${testIdPrefix}-row-${idx}-qty`}
                />
                {item?.unit && (
                  <span className="text-[10px] text-gray-500 min-w-[24px]">{item.unit}</span>
                )}
                <button
                  type="button"
                  onClick={() => handleRemove(idx)}
                  className="p-1 rounded hover:bg-red-50 text-gray-400 hover:text-red-600 transition-colors"
                  title="Remove"
                  data-testid={`${testIdPrefix}-row-${idx}-remove`}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {showAdder ? (
        <div className="mt-2 rounded-md border border-gray-200 bg-white p-2 space-y-1.5">
          <div className="flex items-center gap-2">
            <Input
              value={addQuery}
              onChange={(e) => setAddQuery(e.target.value)}
              placeholder={loading ? 'Loading catalog…' : 'Search catalog items…'}
              className="text-xs h-7"
              autoFocus
              data-testid={`${testIdPrefix}-search`}
            />
            <button
              type="button"
              onClick={() => { setShowAdder(false); setAddQuery(''); }}
              className="text-[11px] text-gray-500 hover:text-gray-700 px-1"
            >
              Done
            </button>
          </div>
          {!loading && addableOptions.length === 0 && (
            <div className="text-[11px] text-gray-500 py-1" data-testid={`${testIdPrefix}-no-results`}>
              {allItems.length === 0
                ? 'No catalog items yet. Create one in Settings → Catalog Items.'
                : addQuery
                  ? 'No matching active items.'
                  : 'All active items already selected.'}
            </div>
          )}
          {addableOptions.length > 0 && (
            <div className="max-h-40 overflow-y-auto -mx-1" data-testid={`${testIdPrefix}-options`}>
              {addableOptions.map((it) => (
                <button
                  key={it.id}
                  type="button"
                  onClick={() => handleAdd(it.id)}
                  className="w-full text-left px-2 py-1.5 rounded hover:bg-gray-50 flex items-center gap-2"
                  data-testid={`${testIdPrefix}-option-${it.id}`}
                >
                  <Package className="w-3 h-3 text-gray-400 flex-shrink-0" />
                  <span className="text-xs text-gray-800 truncate flex-1">{it.name}</span>
                  {it.sku && <span className="text-[10px] text-gray-400 flex-shrink-0">{it.sku}</span>}
                  {it.unit && <span className="text-[10px] text-gray-400 flex-shrink-0">{it.unit}</span>}
                </button>
              ))}
            </div>
          )}
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setShowAdder(true)}
          className="mt-1.5 flex items-center gap-1.5 text-xs text-gray-600 hover:text-gray-900 border border-dashed border-gray-300 rounded-md px-2 py-1.5 hover:border-gray-400 hover:bg-gray-50 transition-colors"
          data-testid={`${testIdPrefix}-add-btn`}
        >
          <Plus className="w-3.5 h-3.5" />
          Add item
        </button>
      )}
    </div>
  );
};

export default DefaultItemsPicker;
